import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export enum NotificationType {
  ADD_TO_CART = 'ADD_TO_CART',
  REMOVED_FROM_CART = 'REMOVED_FROM_CART',
  ERROR = 'ERROR',
  WARNING = 'WARNING',
  SUCCESS = 'SUCCESS',
  INFO = 'INFO',
}

export interface NotificationState {
  type: NotificationType;
  message?: string;
  timeout: number | null;
  expires: number;
  miniCart?: {
    itemCode: string;
    quantity: string;
  };
}

export interface NotificationsStore {
  notifications: NotificationState[];
  addNotification: (notification: NotificationState) => void;
  resetNotification: (notifications: NotificationState[]) => void;
  removeExpired: () => void;
  clear: () => void;
}

const EXPIRATION_CHECK_INTERVAL = 100;
let expiryIntervalId: ReturnType<typeof setInterval> | null = null;

export const useNotificationsStore = create<NotificationsStore>()(
  devtools(
    (set, get) => ({
      notifications: [],
      addNotification: notification =>
        set(state => ({ notifications: [notification, ...state.notifications] }), undefined, 'addNotification'),
      resetNotification: notifications =>
        set({ notifications }, undefined, 'resetNotification'),
      removeExpired: () => {
        const now = Date.now();
        const current = get().notifications;
        const updated = current.filter(n => n.expires > now);
        if (current.length !== updated.length) {
          set({ notifications: updated }, undefined, 'removeExpired');
        }
      },
      clear: () => set({ notifications: [] }, undefined, 'clear'),
    }),
    { name: 'NotificationsStore' },
  ),
);

export const selectAddToCartNotification = (state: NotificationsStore): NotificationState | undefined =>
  state.notifications.find(n => [NotificationType.ADD_TO_CART, NotificationType.REMOVED_FROM_CART].includes(n.type));

export const getAddToCartNotification = (): NotificationState | undefined =>
  selectAddToCartNotification(useNotificationsStore.getState());

/** Start the auto-expiry interval. Safe to call multiple times. */
export function startNotificationExpiry(): void {
  if (expiryIntervalId !== null) {
    return;
  }
  expiryIntervalId = setInterval(() => {
    useNotificationsStore.getState().removeExpired();
  }, EXPIRATION_CHECK_INTERVAL);
}

/** Stop the auto-expiry interval. */
export function stopNotificationExpiry(): void {
  if (expiryIntervalId !== null) {
    clearInterval(expiryIntervalId);
    expiryIntervalId = null;
  }
}
